import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGOUT, SETQRIMAGE, REQUIRESETPASSWORD, SET_INITLOGIN } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

import {
    userLoginRequest,
    verifyLoginOtp,
    verifySession,
    logout as logoutSession,
    resendOtp as resendLoginOtp,
    changePassword,
    requestResetPassword,
    userValidateInitLogin,
	validateInitialLoginInvitation,
    setPassword
} from 'api/auth';
import { IdleTimerProvider } from 'react-idle-timer';
import { getDeployment } from 'utils/deployment';
import { LOGIN_USER_TYPES } from 'constants';

const chance = new Chance();
const getLoginTypeFromPrincipal = (principalType = '') =>
    principalType === 'candidate'
        ? LOGIN_USER_TYPES.CANDIDATE
        : principalType === 'guest'
        ? LOGIN_USER_TYPES.GUEST
        : LOGIN_USER_TYPES.ASSESSOR;

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    isSessionTimeout: false,
    otpCode: false,
    qrImage: null,
    isKeepMeLogin: false,
    initLogin: false,
    requireSetPassword: false,
    deployments: []
};

// const setSession = (serviceToken, loginType = LOGIN_TYPE['learner']) => {
//     if (serviceToken) {
//         localStorage.setItem('serviceToken', serviceToken);
//         axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

//         // for login type
//         localStorage.setItem('loginType', loginType);
//     } else {
//         localStorage.removeItem('serviceToken');
//         delete axios.defaults.headers.common.Authorization;

//         localStorage.removeItem('loginType');
//     }
// };

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
export const setOTPSessionKey = (sessionKey) => {
    if (sessionKey) {
        localStorage.setItem('otpSessionKey', sessionKey);
    }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    /**
     * Handle Session Timeout detection
     * To show re enter password modal
     */
    const handleSessionTimeout = (user) => {
        const exp = user?.exp;
        const now = new Date();
        const timeoutMilliSeconds = exp * 1000 - now.getTime();

        if (timeoutMilliSeconds > 0) {
            // trigger the timeout
            setTimeout(() => {
                // redirect to logout if temporary access as admin
                if (user?.isAccessAdmin) {
                    dispatch({ type: LOGOUT });
                }
                dispatch({
                    type: 'SESSION_TIMEOUT',
                    payload: {
                        isSessionTimeout: true
                    }
                });
            }, timeoutMilliSeconds);
        }
    };

    const initialize = async () => {
        try {
            const response = await verifySession();
            if (response?.success) {
                const { user, deployments = [] } = response;
                dispatch({
                    type: 'LOGIN',
                    payload: {
                        isLoggedIn: true,
                        user,
                        deployments,
                        loginType: getLoginTypeFromPrincipal(user?.principalType)
                    }
                });
                handleSessionTimeout(user);
                dispatch({
                    type: REQUIRESETPASSWORD,
                    payload: {
                        requireSetPassword: response?.requireSetPassword || false
                    }
                });
            } else {
                dispatch({ type: LOGOUT });
            }
        } catch (err) {
            console.log('err', err);
            dispatch({ type: LOGOUT });
        }
    };

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * isRefresh - used for session timeout continue using application.
     */
    const login = async ({
        email,
        password,
        isRefresh = false,
        hasCaptcha = false,
        isKeepMeLogin = false,
        type = '',
        assessmentCode = '',
        requestCode = ''
    }) => {
        const response = await userLoginRequest({ email, password, isRefresh, hasCaptcha, type, assessmentCode, requestCode });

        if (!response) {
            throw new Error('Login failed');
        }

        if (!response?.success) throw new Error(response.message || 'Login failed');

        if (response?.data?.otpSessionKey) {
            setOTPSessionKey(response.data.otpSessionKey);
        }

        // if is Refresh we will recall the handle session timeout
        if (isRefresh) {
            dispatch({
                type: 'SESSION_TIMEOUT',
                payload: {
                    isSessionTimeout: false
                }
            });
            handleSessionTimeout(response?.user);
        } else {
            dispatch({
                type: 'LOGIN_REQUEST_SUCCESS',
                payload: {
                    user: response.user,
                    otpCode: response.user?.otpCode || false,
                    qrImage: response.user?.qrImage || null,
                    isKeepMeLogin,
                    loginType: type
                }
            });
        }
        return response;
    };

    const register = async (email, password, firstName, lastName) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axios.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = (email = null) => {
        if (email) {
            logoutSession({ email });
        }
        dispatch({ type: LOGOUT });

        // reset also initLogin to false if call logout
        dispatch({
            type: SETQRIMAGE,
            payload: { initLogin: false }
        });
    };

    const resetPassword = async (identifier, password, type = '', assessmentId = '', resetPasswordToken = '') => {
        let result = null;
        if (typeof password === 'undefined') {
            result = await requestResetPassword({
                email: identifier,
                type: type || state?.loginType || ''
            });
            return true;
        }

        result = await changePassword(identifier, password, type, assessmentId, resetPasswordToken);
        return result;
    };

    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    const verifyOtp = async ({ email, otp, method, hasCaptcha = false, initialLoginToken = '' }) => {
        const otpSessionKey = localStorage.getItem('otpSessionKey');
        const response = await verifyLoginOtp({
            email,
            otp,
            method,
            hasCaptcha,
            otpSessionKey,
            isKeepMeLogin: state?.isKeepMeLogin,
            initialLoginToken,
            type: state?.loginType || '',
            assessmentId: state?.user?.assessmentId || '',
            requestCode: state?.user?.requestCode || ''
        });
        if (!response) {
            throw new Error('Verification failed');
        }

        const session = await processSuccessLogin();

        dispatch({
            type: REQUIRESETPASSWORD,
            payload: {
                requireSetPassword: response?.data?.requireSetPassword || false
            }
        });

        return {
            ...response,
            authenticatedUser: session.user
        };
    };

    const resendOtp = async (id) => {
        const response = await resendLoginOtp(id, state?.loginType || '');
        if (!response) {
            throw new Error('Resend OTP failed');
        }

        if (response.data) {
            // set the otpSessionKey
            const { otpSessionKey } = response.data;
            setOTPSessionKey(otpSessionKey);
        }
        return response;
    };

    // Inactivity auto logout
    const handleOnIdle = async () => {
        // do only if pathname is not login
        if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            const { user } = state;
            logout(user?.email);
        }
    };

    // validate initial login token and email
    const validateInitLogin = async (email, token, type = '', isAdmin = false) => {
        const response = await userValidateInitLogin(email, token, type, isAdmin);
        const { id, tempSecret, otpSessionKey } = response.data;
        setOTPSessionKey(otpSessionKey);
        dispatch({
            type: SETQRIMAGE,
            payload: { id, tempSecret, initLogin: true }
        });

        // dispatch login request for resend OTP
        dispatch({
            type: 'LOGIN_REQUEST_SUCCESS',
            payload: {
                user: { _id: id, email },
                loginType: type
            }
        });
    };

    const validateInitialInvitation = async (email, token, type = '', isAdmin = false) =>
        await validateInitialLoginInvitation(email, token, type, isAdmin);

    const setUserPassword = async (password, assessmentId = '') => {
        try {
            const data = await setPassword(password, assessmentId);
            dispatch({
                type: REQUIRESETPASSWORD,
                payload: {
                    requireSetPassword: false
                }
            });
            return data;
        } catch (e) {
            throw new Error(e.message);
        }
    };

    const processSuccessLogin = async () => {
        const resolvedDeployment = getDeployment();

        if (resolvedDeployment) {
            localStorage.setItem('active-deployment', resolvedDeployment);
        }

        /**
         * Call again the verify session for user information
         */
        const sessionResponse = await verifySession();

        if (!sessionResponse?.success) {
            dispatch({ type: LOGOUT });
            throw new Error(sessionResponse?.message || 'Unable to verify session');
        }

        const { user, deployments = [] } = sessionResponse;

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
                deployments,
                loginType: getLoginTypeFromPrincipal(user?.principalType) || state?.loginType
            }
        });

        handleSessionTimeout(user);

        return sessionResponse;
    };

    /**
     * If success change password via Invite Candidate
     * This will show an otp modal for Email
     * Note: Can Reuse for other Role
     */
    const setInitLogin = async (otpSessionKey, user, loginType) => {
        setOTPSessionKey(otpSessionKey);
        dispatch({
            type: SET_INITLOGIN,
            payload: {
                initLogin: true,
                user,
                loginType
            }
        });
    };

    return (
        <IdleTimerProvider
            timeout={1000 * 60 * (process.env.REACT_APP_INACTIVITY_TIMEOUT || 45)}
            onIdle={handleOnIdle}
            crossTab={true}
            name="MY_APP_IDLE_TIMER"
        >
            <JWTContext.Provider
                // eslint-disable-next-line
                value={{
                    ...state,
                    login,
                    logout,
                    register,
                    resetPassword,
                    updateProfile,
                    verifyOtp,
                    refetchSession: initialize,
                    resendOtp,
                    validateInitLogin,
					validateInitialInvitation,
                    setUserPassword,
                    setInitLogin
                }}
            >
                {children}
            </JWTContext.Provider>
        </IdleTimerProvider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
